import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useOkapiKy } from '@folio/stripes/core';
import { useStripes } from '@folio/stripes/core';

export default function useFavoriteReportsAPI() {
  const okapiKy = useOkapiKy();
  const stripes = useStripes();
  const queryClient = useQueryClient();
  const userId = stripes.user?.user?.id;

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['favorite-reports', userId],
    queryFn: async () => {
      const response = await okapiKy.get(`favorite-reports?query=userId==${userId}&limit=1000`).json();
      return response;
    },
    enabled: !!userId,
    staleTime: 5 * 60 * 1000,
  });

  const addFavorite = useMutation({
    mutationFn: async ({ reportId, tags = [], notes = '', displayOrder = 0 }) => {
      return await okapiKy.post('favorite-reports', {
        json: {
          reportId,
          userId,
          tags,
          notes,
          displayOrder
        }
      }).json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['favorite-reports', userId]);
    }
  });

  const removeFavorite = useMutation({
    mutationFn: async (favoriteId) => {
      return await okapiKy.delete(`favorite-reports/${favoriteId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['favorite-reports', userId]);
    }
  });

  const updateFavorite = useMutation({
    mutationFn: async ({ favoriteId, updates }) => {
      return await okapiKy.put(`favorite-reports/${favoriteId}`, {
        json: updates
      }).json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['favorite-reports', userId]);
    }
  });

  const isFavorite = (reportId) => {
    return data?.favoriteReports?.some(fav => fav.reportId === reportId);
  };

  const getFavoriteId = (reportId) => {
    return data?.favoriteReports?.find(fav => fav.reportId === reportId)?.id;
  };

  return {
    favoriteReports: data?.favoriteReports || [],
    totalRecords: data?.totalRecords || 0,
    isLoading,
    error,
    refetch,
    addFavorite,
    removeFavorite,
    updateFavorite,
    isFavorite,
    getFavoriteId
  };
}
